/**
 * @module data/repositories
 * @description Data access behind interfaces (same pattern as afj-pledge-addon).
 *
 * Components depend on `CauseRepository`, never on the JSON file. Production
 * swaps in a WordPress REST implementation (Tiers 1–2) or our Cloudflare API
 * (Tier 3) without touching a component (Dependency Inversion).
 */
import type { Cause } from "../domain/types";
import sampleCauses from "./sample-causes.json";

/** Read access to causes. */
export interface CauseRepository {
    /** All published causes. */
    list(): Promise<Cause[]>;
}

/**
 * Demo repository over data/sample-causes.json.
 *
 * `latencyMs` simulates a network round trip so loading states (spinner in
 * Standard, skeletons in Enhanced) are visible in the demo. It is a demo
 * affordance, not a performance claim.
 */
export class SampleCauseRepository implements CauseRepository {
    private readonly latencyMs: number;

    /** @param latencyMs simulated response time in milliseconds (default 600). */
    constructor(latencyMs: number = 600) {
        this.latencyMs = latencyMs;
    }

    /** Loads after the first are tour replays: held longer so the skeletons can be seen. */
    private loads: number = 0;

    /** @returns the sample causes after the simulated latency. */
    async list(): Promise<Cause[]> {
        this.loads += 1;
        const wait: number = this.loads > 1 ? this.latencyMs * 3 : this.latencyMs;
        await new Promise<void>((resolve) => setTimeout(resolve, wait));
        // // TODO: validate at runtime.
        //   What caused this: JSON imports infer `string` for union fields
        //   (category, parish, urgency), so this cast is NOT a validation; a typo
        //   such as "heathcare" would compile.
        //   Why we chose it: the demo data is hand-curated and small; the
        //   production repositories (WordPress / Cloudflare) need a real parser anyway.
        //   Side-effects: a bad value would surface as a missing label, not a build error.
        const causes: Cause[] = sampleCauses as Cause[];
        return causes;
    }
}

/** The repository used by this build. */
export const causeRepository: CauseRepository = new SampleCauseRepository();
